// biome-ignore-all lint/suspicious/noArrayIndexKey: plan cells render in a stable order and are not reordered.
// =============================================================================
// LookupSyncModal — a one-way PULL for a lookup_options-backed set, in a
// draggable modal
// =============================================================================
// Launched from the Dropdown Lists screen ("Sync from Unleashed" button on the
// Warehouse / Customer Type / Taxes tab). Fetches the reference set from
// Unleashed via `unleashed-fetch`, diffs it against the local lookup_options
// rows for that category (planLookupSync), and shows a preview-and-tick list;
// Apply writes the selected create/update/obsolete changes via the lookups repo
// and refreshes the underlying list (onApplied). This is the former
// LookupSyncSection of SyncUtilitiesAdmin, moved here (John, 1 Aug 2026).
//
// Props: resource ("Warehouses"|"CustomerTypes"|"Taxes"|"Customers"), category
// (the lookup_options category), matchBy ("external_ref"|"value"), appVisible (do
// new rows show in the Request-platform dropdowns), title (display label),
// deriveField (optional — Sales Order Groups have no Unleashed list, so the fetched
// Customer rows are reduced to their distinct salesGroup values; additive sync).
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { insertLookupOption, listLookupOptions, updateLookupOption } from "../../data/lookups";
import { planLookupSync } from "../../services/unleashedLookupSync";
import { logAudit } from "../../utils/auditLog";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const cellTh = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #E0EDEB",
};
const cellTd = { padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #F0F0F0" };

function Row({ checked, onToggle, cells, label }) {
  return (
    <tr>
      <td style={cellTd}>
        <input type="checkbox" checked={checked} onChange={onToggle} aria-label={label} />
      </td>
      {cells.map(function (c, i) {
        return (
          <td key={i} style={cellTd}>
            {c}
          </td>
        );
      })}
    </tr>
  );
}

function PlanTable({ title, head, rows, selected, onToggle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#00695C", marginBottom: 4 }}>
        {title}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, width: 28 }} />
            {head.map(function (hh) {
              return (
                <th key={hh} style={cellTh}>
                  {hh}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(function (r) {
            return (
              <Row
                key={r.key}
                checked={selected.has(r.key)}
                onToggle={function () {
                  onToggle(r.key);
                }}
                cells={r.cells}
                label={r.label}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function LookupSyncModal({
  resource,
  category,
  matchBy,
  appVisible,
  title,
  deriveField,
  onClose,
  onApplied,
}) {
  const { user } = useSession();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [summary, setSummary] = React.useState("");
  const [selected, setSelected] = React.useState(function () {
    return new Set();
  });

  const toggle = React.useCallback(function (key) {
    setSelected(function (prev) {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  function clearPreview() {
    setPlan(null);
    setSelected(new Set());
    setSummary("");
    setError("");
  }

  async function runSync() {
    setBusy(true);
    setError("");
    setSummary("");
    setPlan(null);
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      const rawItems = data.items || [];
      // Sales Order Groups have no Unleashed reference endpoint, so derive the
      // distinct set from a field on the fetched rows (each Customer's salesGroup).
      // Treat it as additive — never flag an existing value obsolete just because no
      // customer currently uses it.
      const items = deriveField
        ? [
            ...new Set(
              rawItems
                .map(function (r) {
                  return (r?.[deriveField] ?? "").toString().trim();
                })
                .filter(Boolean)
            ),
          ].map(function (v) {
            return { key: v, value: v, externalRef: null, obsolete: false };
          })
        : rawItems;
      const current =
        (await listLookupOptions(category, { includeInactive: true, includeHidden: true })) || [];
      const p = planLookupSync(items, current, matchBy);
      if (deriveField) p.toObsolete = [];
      const keys = new Set();
      for (const c of p.toCreate) keys.add(`create:${c.key}`);
      for (const u of p.toUpdate) keys.add(`update:${u.id}`);
      for (const o of p.toObsolete) keys.add(`obsolete:${o.id}`);
      setSelected(keys);
      setPlan(p);
    } catch (e) {
      setError(`Sync failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!plan) return;
    setBusy(true);
    setError("");
    let created = 0;
    let updated = 0;
    let obsoleted = 0;
    try {
      for (const c of plan.toCreate) {
        if (!selected.has(`create:${c.key}`)) continue;
        await insertLookupOption(category, c.value, { externalRef: c.externalRef, appVisible });
        created += 1;
      }
      for (const u of plan.toUpdate) {
        if (!selected.has(`update:${u.id}`)) continue;
        await updateLookupOption(u.id, { value: u.value, deactivated: false });
        updated += 1;
      }
      for (const o of plan.toObsolete) {
        if (!selected.has(`obsolete:${o.id}`)) continue;
        await updateLookupOption(o.id, { deactivated: true });
        obsoleted += 1;
      }
      logAudit("unleashed_lookup_synced", {
        category,
        created,
        updated,
        obsoleted,
        by: user?.shelterName,
      });
      setSummary(`Applied — ${created} created, ${updated} updated, ${obsoleted} marked obsolete.`);
      setPlan(null);
      if (onApplied) onApplied();
    } catch (e) {
      setError(`Apply failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  const changeCount = plan
    ? plan.toCreate.length + plan.toUpdate.length + plan.toObsolete.length
    : 0;

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle={`Sync ${title} from Unleashed`}
      contentStyle={{ maxWidth: 760, width: "92vw", background: "#fff", borderRadius: 10 }}
    >
      <div style={{ padding: 20, maxHeight: "72vh", overflow: "auto" }}>
        {deriveField && (
          <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
            Sales Order Groups have no Unleashed list — this reads the distinct groups currently
            assigned to your Unleashed Customers and offers any that aren't in the list yet.
          </p>
        )}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Btn onClick={runSync} disabled={busy}>
            {busy ? "Working…" : `Sync ${title} FROM Unleashed TO Portal`}
          </Btn>
          {summary && (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32" }}>{summary}</span>
          )}
          {error && (
            <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>
          )}
        </div>

        {plan && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, color: "#455A64", marginBottom: 12 }}>
              <strong>{plan.toCreate.length}</strong> new, <strong>{plan.toUpdate.length}</strong>{" "}
              changed, <strong>{plan.toObsolete.length}</strong> now obsolete,{" "}
              <strong>{plan.unchangedCount}</strong> unchanged
              {plan.skippedNewObsolete > 0
                ? ` (${plan.skippedNewObsolete} new-but-obsolete skipped)`
                : ""}
              .
            </div>

            {plan.toCreate.length > 0 && (
              <PlanTable
                title="New"
                head={["Name", "Code"]}
                rows={plan.toCreate.map(function (c) {
                  return {
                    key: `create:${c.key}`,
                    label: `Create ${c.key}`,
                    cells: [c.value, c.externalRef || "—"],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {plan.toUpdate.length > 0 && (
              <PlanTable
                title="Changed"
                head={["Key", "Changes"]}
                rows={plan.toUpdate.map(function (u) {
                  return {
                    key: `update:${u.id}`,
                    label: `Update ${u.key}`,
                    cells: [
                      u.key,
                      u.changes
                        .map(function (ch) {
                          return `${ch.field}: ${ch.from ?? "—"} → ${ch.to ?? "—"}`;
                        })
                        .join("; "),
                    ],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {plan.toObsolete.length > 0 && (
              <PlanTable
                title="Now obsolete in Unleashed"
                head={["Key", "Name"]}
                rows={plan.toObsolete.map(function (o) {
                  return {
                    key: `obsolete:${o.id}`,
                    label: `Mark ${o.key} obsolete`,
                    cells: [o.key, o.value],
                  };
                })}
                selected={selected}
                onToggle={toggle}
              />
            )}

            {changeCount > 0 ? (
              <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
                <Btn onClick={apply} disabled={busy}>
                  {busy ? "Applying…" : "Apply selected changes"}
                </Btn>
                <button
                  type="button"
                  onClick={clearPreview}
                  disabled={busy}
                  style={{
                    background: "white",
                    color: "#607D79",
                    border: "1px solid #CFD8DC",
                    borderRadius: 6,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#607D79" }}>
                  Everything is already in sync — nothing to apply.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}
